import { ArrowUpRight, ChevronDown, History, Send, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { AskAnswer, AttentionObject } from "@aion/xo-core";

import { DEMO_MODE } from "../hooks";
import { askXO, type AskStreamResult } from "../lib/api";
import { ProviderIcon } from "./ProviderIcon";

const suggestions = ["Who is waiting on me?", "What changed?", "Why this priority?"];

export const AskPanel = ({
  attentionObjects,
  currentSources,
  onClose,
  open,
}: {
  attentionObjects: AttentionObject[];
  currentSources: number;
  onClose: () => void;
  open: boolean;
}) => {
  const [question, setQuestion] = useState("");
  const [submittedQuestion, setSubmittedQuestion] = useState("What can I safely postpone?");
  const [answer, setAnswer] = useState<AskStreamResult | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose, open]);

  const demoAnswer = useMemo<AskAnswer>(() => {
    const top = attentionObjects.slice(0, 3);
    return {
      answer:
        "Move the launch-summary draft to tomorrow.\n\nKeep the Rennes tuition confirmation today. The conflicting instructions could affect access.\n\nThe TGV luggage decision can wait until this afternoon, but not beyond today.",
      why: "Capacity · consequence · actionability",
      citations: top.flatMap((item) =>
        item.evidence.slice(0, 1).map((evidence) => ({
          attentionObjectId: item.id,
          evidenceRefId: evidence.id,
          label: evidence.label,
          provider: evidence.provider,
          url: evidence.url,
        })),
      ),
    };
  }, [attentionObjects]);

  useEffect(() => {
    if (DEMO_MODE && !answer) {
      setAnswer({ conversationId: "demo", ...demoAnswer });
    }
  }, [answer, demoAnswer]);

  const send = async (value = question) => {
    const trimmed = value.trim();
    if (!trimmed || status) return;
    setSubmittedQuestion(trimmed);
    setQuestion("");
    setError(null);
    setStatus("retrieving");
    try {
      if (DEMO_MODE) {
        await new Promise((resolve) => window.setTimeout(resolve, 350));
        setAnswer({ conversationId: "demo", ...demoAnswer });
      } else {
        const result = await askXO({
          question: trimmed,
          conversationId,
          onStatus: setStatus,
        });
        setAnswer(result);
        setConversationId(result.conversationId);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "XO could not answer.");
    } finally {
      setStatus(null);
    }
  };

  if (!open) return null;

  return (
    <div className="ask-layer" onMouseDown={onClose} role="presentation">
      <aside aria-label="Ask XO" aria-modal="true" className="ask-panel" onMouseDown={(event) => event.stopPropagation()} role="dialog">
      <header>
        <div><h2>Ask XO</h2><span><i /> {currentSources} sources current</span></div>
        <button aria-label="Clear conversation" onClick={() => { setAnswer(null); setConversationId(undefined); }} type="button"><History /></button>
        <button aria-label="Close Ask XO" onClick={onClose} type="button"><X /></button>
      </header>
      <div className="ask-panel__conversation">
        <p className="ask-question">{submittedQuestion}</p>
        {status ? <p className="ask-status">XO is {status}…</p> : null}
        {error ? <p className="panel-error">{error}</p> : null}
        {answer ? (
          <>
            <div className="ask-answer">
              {answer.answer.split("\n\n").map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
            <section className="ask-why"><strong>Why</strong><span>{answer.why}</span><ChevronDown /></section>
            <section className="ask-sources">
              <h3>Sources</h3>
              {answer.citations.map((citation) => (
                <a href={citation.url || undefined} key={citation.evidenceRefId} rel="noreferrer" target="_blank">
                  <ProviderIcon provider={citation.provider} />
                  <span><strong>{citation.label}</strong><small>{citation.provider}</small></span>
                  <ArrowUpRight />
                </a>
              ))}
            </section>
          </>
        ) : null}
      </div>
      <div className="ask-panel__composer">
        <span>Suggested</span>
        <div className="ask-suggestions">
          {suggestions.map((suggestion) => (
            <button key={suggestion} onClick={() => void send(suggestion)} type="button">{suggestion}</button>
          ))}
        </div>
        <form onSubmit={(event) => { event.preventDefault(); void send(); }}>
          <input aria-label="Ask about your operational world" onChange={(event) => setQuestion(event.target.value)} placeholder="Ask about your operational world…" value={question} />
          <button aria-label="Send question" disabled={!question.trim() || Boolean(status)} type="submit"><Send /></button>
        </form>
      </div>
      </aside>
    </div>
  );
};
